import {
  createFileRoute,
  Outlet,
  useBlocker,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { ExamTopBar } from '@/components/ui/ExamTopBar';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { LoadingLine, Spinner } from '@/components/ui/States';
import { ListeningSection } from '@/features/listening/ListeningSection';
import { SequentialSection } from '@/features/sequential/SequentialSection';
import { SpeakingSection } from '@/features/speaking/SpeakingSection';
import { WritingSection } from '@/features/writing/WritingSection';
import { ConnectivityBanner } from '@/features/reading/ConnectivityBanner';
import { ReadingSection } from '@/features/reading/ReadingSection';
import { SubmitDialog } from '@/features/reading/QuestionPalette';
import { AttemptEnded } from '@/features/runner/AttemptEnded';
import { LeaveTestModal } from '@/features/runner/LeaveTestModal';
import { RunnerToolbar, useTextScale } from '@/features/runner/RunnerToolbar';
import { api } from '@/api/client';
import { createDeliveryApi } from '@/exam-runtime/api';
import { setExamSessionOnFinished, useExamSession } from '@/exam-runtime/session-hook';
import { examKeyFromFamilySlug, useTheme } from '@/lib/theme';

export const Route = createFileRoute('/attempts/$attemptId')({
  component: AttemptPage,
});

function AttemptStatus({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-[60dvh] items-center justify-center p-4">
      <Card padding="lg" className="w-full max-w-prose">
        {typeof children === 'string' ? <LoadingLine>{children}</LoadingLine> : children}
      </Card>
    </div>
  );
}

function AttemptPage() {
  const { attemptId } = Route.useParams();
  const navigate = useNavigate();
  const isResultsRoute = useRouterState({
    select: (state) =>
      state.matches.some((match) => match.routeId === '/attempts/$attemptId/results'),
  });
  const { setExam } = useTheme();
  const deliveryApi = useMemo(() => createDeliveryApi(api), []);
  const phase = useExamSession((state) => state.phase);
  const timeCredit = useExamSession((state) => state.timeCredit);
  const dismissTimeCredit = useExamSession((state) => state.dismissTimeCredit);
  const advancing = useExamSession((state) => state.advancing);
  const attempt = useExamSession((state) => state.attempt);
  const responses = useExamSession((state) => state.responses);
  const flags = useExamSession((state) => state.flags);
  const activeItemVersionId = useExamSession((state) => state.activeItemVersionId);
  const connectivity = useExamSession((state) => state.connectivity);
  const remainingMs = useExamSession((state) => state.remainingMs);
  const playback = useExamSession((state) => state.playback);
  const error = useExamSession((state) => state.error);
  const load = useExamSession((state) => state.load);
  const answer = useExamSession((state) => state.answer);
  const toggleFlag = useExamSession((state) => state.toggleFlag);
  const setActive = useExamSession((state) => state.setActive);
  const startPlayback = useExamSession((state) => state.startPlayback);
  const startItem = useExamSession((state) => state.startItem);
  const advanceSection = useExamSession((state) => state.advanceSection);
  const submit = useExamSession((state) => state.submit);
  const dispose = useExamSession((state) => state.dispose);

  const [submitOpen, setSubmitOpen] = useState(false);
  const [textScale, setTextScale] = useTextScale();
  const [leaving, setLeaving] = useState(false);

  const running = phase === 'in_progress' && attempt?.status === 'in_progress';
  const blocker = useBlocker({
    // Moving between this test and its own results is fine; going anywhere else ends the test.
    shouldBlockFn: ({ next }) => !next.routeId.startsWith('/attempts/$attemptId'),
    enableBeforeUnload: () => running,
    disabled: !running,
    withResolver: true,
  });

  useEffect(() => {
    if (isResultsRoute) return;

    setExamSessionOnFinished((finishedAttemptId) => {
      void navigate({
        to: '/attempts/$attemptId/results',
        params: { attemptId: finishedAttemptId },
      });
    });
    void load(attemptId);
    return () => {
      setExamSessionOnFinished(null);
      dispose();
    };
  }, [attemptId, dispose, isResultsRoute, load, navigate]);

  const section = useMemo(() => {
    if (!attempt || attempt.currentSectionIndex === null) return null;
    return attempt.sections[attempt.currentSectionIndex] ?? null;
  }, [attempt]);

  useEffect(() => {
    if (!attempt) return;
    setExam(examKeyFromFamilySlug(attempt.variant.slug));
  }, [attempt, setExam]);

  useEffect(() => {
    if (!section || section.delivery === 'sequential' || activeItemVersionId) return;
    const first = [...section.items].sort((a, b) => a.sortOrder - b.sortOrder)[0];
    if (first) setActive(first.itemVersionId);
  }, [activeItemVersionId, section, setActive]);

  const unansweredCount = useMemo(() => {
    if (!section) return 0;
    return section.items.filter((item) => {
      const value = responses[item.itemVersionId];
      if (value === null || value === undefined) return true;
      if (typeof value === 'object' && value !== null && 'mediaAssetId' in value) {
        const mediaAssetId = Reflect.get(value, 'mediaAssetId');
        return typeof mediaAssetId !== 'string' || mediaAssetId.length === 0;
      }
      if (typeof value === 'object' && value !== null && 'value' in value) {
        const inner = Reflect.get(value, 'value');
        return inner === null || inner === undefined || inner === '';
      }
      if (typeof value === 'object' && value !== null && 'text' in value) {
        const text = Reflect.get(value, 'text');
        return typeof text !== 'string' || text.trim().length === 0;
      }
      return false;
    }).length;
  }, [responses, section]);

  const leaveTest = async () => {
    setLeaving(true);
    try {
      await api.post('/api/v1/attempts/{attemptId}/abandon', { params: { attemptId } });
    } catch {
      // The server also closes a test that has gone quiet, so leaving still works.
    }
    blocker.proceed?.();
  };

  if (!isResultsRoute && attempt?.status === 'abandoned') {
    return <AttemptEnded />;
  }

  // Results is a child route; skip exam-session loading states and render it directly.
  if (isResultsRoute || phase === 'finished') {
    return <Outlet />;
  }

  if (phase === 'loading' || phase === 'idle') {
    return <AttemptStatus>Opening your test.</AttemptStatus>;
  }

  if (phase === 'error') {
    if (error === 'PLAN_REQUIRED') {
      return (
        <AttemptStatus>
          <div className="grid gap-4">
            <h1 className="font-display text-h3">You need an active plan</h1>
            <p className="text-body text-ink-2">
              Your free time or plan has ended. Your history is safe. Buy a plan to start a new
              test.
            </p>
            <div>
              <Button onClick={() => void navigate({ to: '/pricing' })}>See plans</Button>
            </div>
          </div>
        </AttemptStatus>
      );
    }
    return (
      <AttemptStatus>
        <Alert tone="danger" title="We could not open this test.">
          {error ?? 'Reload the page to try again. Your answers are saved.'}
        </Alert>
      </AttemptStatus>
    );
  }

  if (phase === 'submitting') {
    return <AttemptStatus>Sending your answers. Do not close this tab.</AttemptStatus>;
  }

  if (!attempt) {
    return (
      <AttemptStatus>
        <Alert tone="danger" title="This test could not be loaded.">
          Go back to your dashboard and open it again.
        </Alert>
      </AttemptStatus>
    );
  }

  if (!section) {
    if (attempt.status === 'in_progress' || attempt.status === 'created') {
      return (
        <AttemptStatus>
          <div className="grid gap-4">
            <h1 className="font-display text-h3">Ready to submit?</h1>
            <p className="text-body text-ink-2">
              You have finished every section. Submit to see your score and feedback.
            </p>
            <div>
              <Button onClick={() => void submit()}>Submit test</Button>
            </div>
          </div>
        </AttemptStatus>
      );
    }
    return (
      <AttemptStatus>
        <Alert tone="danger" title="This test could not be loaded.">
          Go back to your dashboard and open it again.
        </Alert>
      </AttemptStatus>
    );
  }

  const sectionIndex = attempt.currentSectionIndex ?? 0;
  const isLastSection = sectionIndex >= attempt.sections.length - 1;
  const submitLabel = isLastSection ? 'Submit' : 'Next section';

  const sectionBody =
    phase === 'in_progress' ? (
      section.delivery === 'sequential' ? (
        <SequentialSection
          attemptId={attempt.attemptId}
          serverClock={attempt.serverClock}
          mode={attempt.mode}
          section={section}
          responses={responses}
          playback={playback}
          onAnswer={answer}
          onStartItem={startItem}
          onStartPlayback={startPlayback}
          onSectionComplete={() => {
            void advanceSection();
          }}
        />
      ) : section.skill === 'listening' ? (
        <ListeningSection
          attemptId={attempt.attemptId}
          mode={attempt.mode}
          section={section}
          responses={responses}
          flags={flags}
          activeItemVersionId={activeItemVersionId}
          playback={playback}
          onAnswer={answer}
          onToggleFlag={toggleFlag}
          onSetActive={setActive}
          onOpenSubmit={() => {
            setSubmitOpen(true);
          }}
          onStartPlayback={startPlayback}
        />
      ) : section.skill === 'reading' ? (
        <ReadingSection
          attemptId={attempt.attemptId}
          mode={attempt.mode}
          section={section}
          responses={responses}
          flags={flags}
          activeItemVersionId={activeItemVersionId}
          onAnswer={answer}
          onToggleFlag={toggleFlag}
          onSetActive={setActive}
          onOpenSubmit={() => {
            setSubmitOpen(true);
          }}
        />
      ) : section.skill === 'writing' ? (
        <WritingSection
          mode={attempt.mode}
          section={section}
          responses={responses}
          flags={flags}
          activeItemVersionId={activeItemVersionId}
          onAnswer={answer}
          onToggleFlag={toggleFlag}
          onSetActive={setActive}
          onOpenSubmit={() => {
            setSubmitOpen(true);
          }}
        />
      ) : section.skill === 'speaking' ? (
        <SpeakingSection
          attemptId={attempt.attemptId}
          attempt={attempt}
          mode={attempt.mode}
          section={section}
          responses={responses}
          flags={flags}
          activeItemVersionId={activeItemVersionId}
          api={deliveryApi}
          onAnswer={answer}
          onToggleFlag={toggleFlag}
          onSetActive={setActive}
          onOpenSubmit={() => {
            setSubmitOpen(true);
          }}
        />
      ) : (
        <Alert tone="danger" title={`We cannot show a ${section.skill} section yet.`} />
      )
    ) : null;

  return (
    <div className="flex min-h-dvh flex-col">
      <ExamTopBar
        variantName={attempt.variant.name}
        skill={section.skill}
        mode={attempt.mode}
        remainingMs={remainingMs}
        trademarkDisclaimer={attempt.variant.trademarkDisclaimer}
        accent={attempt.variant.theme.accent}
        tint={attempt.variant.theme.tint}
        submitLabel={submitLabel}
        onSubmit={() => {
          setSubmitOpen(true);
        }}
      >
        <ConnectivityBanner connectivity={connectivity} />
      </ExamTopBar>

      <main
        className="mx-auto flex min-h-0 w-full max-w-content flex-1 flex-col gap-4 px-4 py-4 sm:px-8"
        style={{ zoom: textScale }}
      >
        <RunnerToolbar scale={textScale} onScale={setTextScale} />
        {sectionBody}
      </main>

      <LeaveTestModal
        open={blocker.status === 'blocked'}
        leaving={leaving}
        onStay={() => {
          blocker.reset?.();
        }}
        onLeave={() => {
          void leaveTest();
        }}
      />

      {timeCredit ? (
        <div className="fixed inset-x-4 bottom-4 z-50 mx-auto max-w-md">
          <Alert tone="info" title={`We added ${creditLabel(timeCredit.grantedMs)} to your time.`}>
            {timeCredit.reason === 'connection_lost'
              ? 'Your connection was lost.'
              : 'The recording did not load.'}{' '}
            <button type="button" className="font-bold underline" onClick={dismissTimeCredit}>
              Got it
            </button>
          </Alert>
        </div>
      ) : null}

      {advancing ? (
        <div
          role="status"
          className="fixed inset-0 z-50 grid place-items-center bg-paper/80 backdrop-blur-[1px]"
        >
          <p className="flex items-center gap-3 rounded-card border border-border bg-surface px-5 py-4 text-body font-bold text-ink">
            <Spinner />
            Saving your answers and opening the next section…
          </p>
        </div>
      ) : null}

      <SubmitDialog
        open={submitOpen}
        unansweredCount={unansweredCount}
        onCancel={() => {
          setSubmitOpen(false);
        }}
        onConfirm={() => {
          setSubmitOpen(false);
          if (isLastSection) {
            void submit();
          } else {
            void advanceSection();
          }
        }}
      />
    </div>
  );
}

function creditLabel(ms: number): string {
  const seconds = Math.round(ms / 1000);
  return seconds >= 60
    ? `${String(Math.floor(seconds / 60))} min ${String(seconds % 60)} s`
    : `${String(seconds)} seconds`;
}
